/* PetiteGalette product video player v2 — no native video fullscreen or native controls. */
(() => {
  'use strict';
  // The snippet occurs once in each responsive placement.
  if (window.pgProductVideoPlayerV2) return;
  window.pgProductVideoPlayerV2 = true;

  const icons = {
    play: '<path d="m9 5 11 7-11 7Z" fill="currentColor" stroke="none"/>',
    pause: '<path d="M8 5v14M16 5v14" stroke-width="4"/>',
    sound: '<path d="M11 5 6 9H3v6h3l5 4Z"/><path d="M15 8a6 6 0 0 1 0 8M18 5a10 10 0 0 1 0 14"/>',
    muted: '<path d="M11 5 6 9H3v6h3l5 4Z"/><path d="m16 9 6 6m0-6-6 6"/>',
    close: '<path d="m6 6 12 12M18 6 6 18"/>'
  };
  const icon = name => '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' + icons[name] + '</svg>';
  let current = null;
  const fullscreenElement = () => document.fullscreenElement || document.webkitFullscreenElement;
  const isCurrent = session => current === session && !session.closed;

  function makeButton(className, label, iconName) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pg-video-player__button ' + className;
    button.setAttribute('aria-label', label);
    button.title = label;
    button.innerHTML = icon(iconName);
    return button;
  }

  function setButton(button, label, iconName) {
    button.setAttribute('aria-label', label);
    button.title = label;
    button.innerHTML = icon(iconName);
  }

  function status(session, message) {
    session.status.textContent = message;
    session.status.hidden = !message;
  }

  function syncControls(session) {
    const paused = session.video.paused || session.video.ended;
    setButton(session.toggle, paused ? 'Play video' : 'Pause video', paused ? 'play' : 'pause');
    const muted = session.video.muted || session.video.volume === 0;
    setButton(session.sound, muted ? 'Turn sound on' : 'Mute sound', muted ? 'muted' : 'sound');
    session.sound.setAttribute('aria-pressed', String(muted));
  }

  function exitFullscreen(session) {
    if (fullscreenElement() !== session.root) return;
    try {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      const result = exit?.call(document);
      result?.catch?.(() => {});
    } catch (_) { /* The browser may have already exited. */ }
  }

  function lockPage(session) {
    session.scroll = { x: window.scrollX, y: window.scrollY };
    session.styles = [];
    const set = (element, property, value) => {
      session.styles.push([element, property, element.style.getPropertyValue(property), element.style.getPropertyPriority(property)]);
      element.style.setProperty(property, value);
    };
    set(document.documentElement, 'overflow', 'hidden');
    set(document.body, 'position', 'fixed');
    set(document.body, 'top', '-' + session.scroll.y + 'px');
    set(document.body, 'left', '-' + session.scroll.x + 'px');
    set(document.body, 'width', '100%');
    set(document.body, 'overflow', 'hidden');
    session.inert = Array.from(document.body.children)
      .filter(element => element !== session.root && !['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName))
      .map(element => [element, element.inert]);
    session.inert.forEach(([element]) => { element.inert = true; });
  }

  function close(session = current, restoreFocus = true) {
    if (!session || session.closed) return;
    session.closed = true;
    if (current === session) current = null;
    session.abort.abort();
    session.video.pause();
    exitFullscreen(session);
    session.video.removeAttribute('src');
    session.video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
    session.video.load();
    session.root.remove();
    session.inert.forEach(([element, value]) => { element.inert = value; });
    session.styles.forEach(([element, property, value, priority]) => {
      if (value) element.style.setProperty(property, value, priority);
      else element.style.removeProperty(property);
    });
    const htmlStyle = document.documentElement.style;
    const scrollBehavior = [htmlStyle.getPropertyValue('scroll-behavior'), htmlStyle.getPropertyPriority('scroll-behavior')];
    htmlStyle.setProperty('scroll-behavior', 'auto', 'important');
    window.scrollTo(session.scroll.x, session.scroll.y);
    if (scrollBehavior[0]) htmlStyle.setProperty('scroll-behavior', ...scrollBehavior);
    else htmlStyle.removeProperty('scroll-behavior');
    if (restoreFocus && session.trigger.isConnected) session.trigger.focus({ preventScroll: true });
  }

  function play(session) {
    if (!isCurrent(session)) return;
    if (session.video.error) {
      session.root.removeAttribute('data-frame-ready');
      session.poster.hidden = !session.poster.getAttribute('src');
      session.video.load();
    }
    status(session, 'Loading video…');
    try {
      const pending = session.video.play();
      pending?.catch?.(() => {
        if (!isCurrent(session)) return;
        syncControls(session);
        status(session, 'Tap Play in the top right to start.');
      });
    } catch (_) {
      syncControls(session);
      status(session, 'Tap Play in the top right to start.');
    }
  }

  function requestFullscreen(session) {
    // Fullscreen the wrapper, never the video: native iPhone video fullscreen adds system controls.
    const request = session.root.requestFullscreen || session.root.webkitRequestFullscreen;
    if (!request || fullscreenElement()) return;
    try {
      const pending = request.call(session.root);
      pending?.then?.(() => {
        if (!isCurrent(session)) exitFullscreen(session);
      })?.catch?.(() => {
        // Custom full-window view remains open when fullscreen is unavailable or denied.
      });
    } catch (_) { /* Keep the full-window view. */ }
  }

  function open(trigger) {
    if (current) return;
    const card = trigger.closest('[data-pg-video-card]');
    const template = card?.querySelector('template[data-pg-video-template]');
    const source = template?.content.querySelector('video');
    if (!source) return;

    const root = document.createElement('div');
    root.className = 'pg-video-player';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Product video');
    root.tabIndex = -1;

    const stage = document.createElement('div');
    stage.className = 'pg-video-player__stage';
    const video = source.cloneNode(true);
    video.className = 'pg-video-player__media';
    video.controls = false;
    video.removeAttribute('controls');
    video.removeAttribute('autoplay');
    video.autoplay = false;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback nofullscreen');
    video.disablePictureInPicture = true;
    video.setAttribute('disablepictureinpicture', '');
    video.disableRemotePlayback = true;
    video.setAttribute('disableremoteplayback', '');
    video.setAttribute('x-webkit-airplay', 'deny');
    video.preload = 'auto';
    video.tabIndex = -1;
    video.muted = false;
    video.defaultMuted = false;
    video.removeAttribute('muted');

    const poster = document.createElement('img');
    poster.className = 'pg-video-player__poster';
    poster.alt = '';
    const thumbnail = trigger.querySelector('img');
    // Reuse the already downloaded thumbnail to avoid a new blank loading phase.
    const posterURL = thumbnail?.currentSrc || thumbnail?.src || video.poster;
    if (posterURL) poster.src = posterURL;
    else poster.hidden = true;
    const message = document.createElement('p');
    message.className = 'pg-video-player__status';
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    stage.append(video, poster, message);

    const toolbar = document.createElement('div');
    toolbar.className = 'pg-video-player__toolbar';
    const dismiss = makeButton('pg-video-player__close', 'Close video', 'close');
    const sound = makeButton('pg-video-player__sound', 'Mute sound', 'sound');
    const toggle = makeButton('pg-video-player__toggle', 'Play video', 'play');
    toolbar.append(dismiss, sound, toggle);
    root.append(stage, toolbar);
    document.body.appendChild(root);

    const session = { root, stage, video, poster, status: message, toggle, sound, dismiss, trigger,
      section: trigger.closest('.shopify-section'), abort: new AbortController(), closed: false, enteredFullscreen: false };
    current = session;
    lockPage(session);
    const on = (target, event, handler, options = {}) =>
      target.addEventListener(event, handler, { ...options, signal: session.abort.signal });

    on(dismiss, 'click', () => close(session));
    on(toggle, 'click', () => {
      if (video.paused || video.ended) play(session);
      else {
        video.pause();
        syncControls(session);
        status(session, '');
      }
    });
    on(sound, 'click', () => {
      const wasMuted = video.muted || video.volume === 0;
      if (wasMuted && video.volume === 0) video.volume = 1;
      video.muted = !wasMuted;
      syncControls(session);
    });
    on(video, 'play', () => syncControls(session));
    on(video, 'pause', () => { syncControls(session); if (!video.error) status(session, ''); });
    on(video, 'volumechange', () => syncControls(session));
    on(video, 'ended', () => { syncControls(session); status(session, ''); });
    on(video, 'waiting', () => { if (!video.paused) status(session, 'Loading video…'); });
    on(video, 'playing', () => {
      syncControls(session);
      const reveal = () => {
        if (!isCurrent(session)) return;
        root.setAttribute('data-frame-ready', '');
        poster.hidden = true;
        status(session, '');
      };
      if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(reveal);
      else requestAnimationFrame(reveal);
    });
    on(video, 'error', () => {
      syncControls(session);
      status(session, 'Video could not load. Tap Play to retry.');
    });
    on(poster, 'error', () => { poster.hidden = true; });
    on(root, 'contextmenu', event => event.preventDefault());
    on(document, 'keydown', event => {
      if (!isCurrent(session)) return;
      if (event.key === 'Escape') { event.preventDefault(); close(session); return; }
      if (event.key !== 'Tab') return;
      const buttons = [dismiss, sound, toggle];
      const index = buttons.indexOf(document.activeElement);
      if (event.shiftKey && index <= 0) { event.preventDefault(); toggle.focus(); }
      else if (!event.shiftKey && (index === buttons.length - 1 || index < 0)) { event.preventDefault(); dismiss.focus(); }
    }, { capture: true });
    on(document, 'focusin', event => {
      if (isCurrent(session) && !root.contains(event.target)) toggle.focus({ preventScroll: true });
    });
    const onFullscreen = () => {
      if (!isCurrent(session)) return;
      if (fullscreenElement() === root) session.enteredFullscreen = true;
      else if (session.enteredFullscreen) close(session);
    };
    on(document, 'fullscreenchange', onFullscreen);
    on(document, 'webkitfullscreenchange', onFullscreen);
    on(window, 'pagehide', () => close(session, false));
    on(document, 'visibilitychange', () => {
      if (document.hidden) video.pause();
    });
    on(document, 'shopify:section:unload', event => {
      if (event.target === session.section || event.target.contains?.(trigger)) close(session, false);
    });
    try { window.pauseAllMedia?.(); } catch (_) { /* Other theme players should not block this player. */ }
    toggle.focus({ preventScroll: true });
    syncControls(session);
    // Both calls run inside the original click. Awaiting either would lose the user gesture.
    play(session);
    requestFullscreen(session);
  }

  document.addEventListener('click', event => {
    const trigger = event.target.closest?.('[data-pg-video-open]');
    if (!trigger) return;
    event.preventDefault();
    open(trigger);
  });
})();
